// components/projects/WorkflowModal.js
// Two-column "Workflow" modal for project types that declare a TYPE_UI entry in config/workflows.js
// (currently Thermal Analysis and Data Sheets). Everything type-specific — the header label, the
// third clock, custom fields, hold support, completion immutability — comes from that config plus
// the field definitions returned by GET /api/projects/:id. Legacy types without a TYPE_UI entry
// continue to use ProjectDetailsModal.
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { WorkflowEngine } from '../../services/workflowEngine';
import { getWorkflow, getTypeUI } from '../../config/workflows';
import { useAlert } from '../common/AppAlert';
import { formatDate, formatDateInput, daysBetween, totalHoldDaysWithinInterval } from '../../utils/dates';

const WorkflowModal = ({ isOpen, onClose, project, user, onProjectUpdate, api }) => {
  const { alert, confirm, prompt } = useAlert();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [taskStates, setTaskStates] = useState({});
  const [pendingTaskChanges, setPendingTaskChanges] = useState(new Set());
  const [isCommitting, setIsCommitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('');
  const [reportPath, setReportPath] = useState('');
  const [projectName, setProjectName] = useState('');
  const [metadata, setMetadata] = useState({});
  const [editedMetadataFields, setEditedMetadataFields] = useState(new Set());
  const [editMode, setEditMode] = useState(false);
  const canEdit = user.isAdmin && editMode;

  const projectType = project?.project_type;
  const ui = getTypeUI(projectType);

  const reload = useCallback(async () => {
    if (!project) return;
    setLoading(true);
    try {
      const data = await api.get(`/projects/${project.id}`, user.token);
      setDetails(data);
      setNotes(data.project.notes || '');
      setPriority(data.project.priority || '');
      setReportPath(data.project.report_path || '');
      setProjectName(data.project.project_name || '');
      setPendingTaskChanges(new Set());
      setEditedMetadataFields(new Set());

      const states = {};
      data.tasks.forEach(task => {
        states[task.task_number] = {
          checked: !!task.is_completed,
          date: task.completed_at ? new Date(task.completed_at).toLocaleDateString() : '',
          user: task.completed_by || '',
          originalState: !!task.is_completed
        };
      });
      const workflow = getWorkflow(project.project_type);
      if (workflow) {
        workflow.tasks.forEach(t => {
          if (!states[t.id]) states[t.id] = { checked: false, date: '', user: '', originalState: false };
        });
      }
      setTaskStates(states);

      const md = {};
      Object.entries(data.metadata || {}).forEach(([k, v]) => { md[k] = v.value || ''; });
      setMetadata(md);
    } catch (e) {
      console.error('Failed to load project details:', e);
    } finally {
      setLoading(false);
    }
  }, [project, user.token, api]);

  useEffect(() => {
    if (isOpen && project) {
      setEditMode(false); // every open starts in view mode
      reload();
    }
  }, [isOpen, project, reload]);

  const completedTaskIds = Object.keys(taskStates).filter(id => taskStates[id]?.checked).map(id => parseInt(id));

  const handleTaskToggle = (taskId) => {
    if (!canEdit) return; // silently no-op in view mode
    if (!WorkflowEngine.taskApplies(projectType, taskId, metadata)) return; // N/A rows are inert
    const cur = taskStates[taskId] || {};

    // Reverting an unsaved in-session check is always allowed — it hasn't become history yet.
    if (cur.checked && cur.originalState === false) {
      setTaskStates(prev => ({
        ...prev,
        [taskId]: { ...prev[taskId], checked: false, date: '', user: '' }
      }));
      setPendingTaskChanges(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      return;
    }

    // Unchecking a committed completion
    if (cur.checked) {
      if (ui?.immutableCompletions) {
        alert(ui.immutableMessage || 'Completed tasks cannot be unchecked for this project type.');
        return;
      }
      const validation = WorkflowEngine.validateTaskChange(projectType, taskId, false, completedTaskIds, metadata);
      if (!validation.valid) {
        alert(validation.error);
        return;
      }
      setTaskStates(prev => ({
        ...prev,
        [taskId]: { ...prev[taskId], checked: false, date: '', user: '' }
      }));
      setPendingTaskChanges(prev => new Set(prev).add(taskId));
      return;
    }

    // Checking a previously-unchecked task
    const validation = WorkflowEngine.validateTaskChange(projectType, taskId, true, completedTaskIds, metadata);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }
    setTaskStates(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        checked: true,
        date: new Date().toLocaleDateString(),
        user: user.displayName
      }
    }));
    setPendingTaskChanges(prev => new Set(prev).add(taskId));
  };

  const setMetadataField = (key, value) => {
    setMetadata(prev => ({ ...prev, [key]: value }));
    setEditedMetadataFields(prev => new Set(prev).add(key));
  };

  const hasUnsavedChanges = () => (
    pendingTaskChanges.size > 0 ||
    editedMetadataFields.size > 0 ||
    notes !== (details?.project?.notes || '') ||
    priority !== (details?.project?.priority || '') ||
    reportPath !== (details?.project?.report_path || '') ||
    (!!details && projectName !== (details.project.project_name || ''))
  );

  const commit = async () => {
    if (!canEdit || !hasUnsavedChanges()) { onClose(); return; }
    setIsCommitting(true);
    try {
      // Task state first (backend auto-populates prereqs_completed_at + linked metadata)
      for (const taskNumber of pendingTaskChanges) {
        const st = taskStates[taskNumber];
        if (st.checked === st.originalState) continue;
        if (st.checked) {
          await api.post(`/projects/${project.id}/tasks/${taskNumber}/complete`, {}, user.token);
        } else {
          await api.post(`/projects/${project.id}/tasks/${taskNumber}/uncomplete`, {}, user.token);
        }
      }

      // Name via its dedicated endpoint — same validation rules as create
      if (details && projectName !== (details.project.project_name || '')) {
        const trimmed = projectName.trim();
        if (!trimmed) { alert('Project name is required'); setIsCommitting(false); return; }
        if (trimmed.includes(',')) {
          alert('Comma character (,) cannot be used - Please revise Project Name');
          setIsCommitting(false);
          return;
        }
        await api.put(`/projects/${project.id}/name`, { project_name: trimmed }, user.token);
      }

      // Only push the metadata fields the user explicitly edited (preserves auto-populated values otherwise)
      if (editedMetadataFields.size > 0) {
        const payload = {};
        editedMetadataFields.forEach(k => { payload[k] = metadata[k]; });
        await api.put(`/projects/${project.id}/metadata`, payload, user.token);
      }

      // Notes / priority / report_path bundled into the existing endpoint
      if (notes !== (details?.project?.notes || '')
        || priority !== (details?.project?.priority || '')
        || reportPath !== (details?.project?.report_path || '')) {
        if (reportPath && reportPath.trim()) {
          try { new URL(reportPath.trim()); }
          catch { alert('Invalid URL format for report path'); setIsCommitting(false); return; }
        }
        await api.put(`/projects/${project.id}/notes`, { notes, priority, report_path: reportPath }, user.token);
      }

      onProjectUpdate && onProjectUpdate();
      onClose();
    } catch (e) {
      alert('Error committing changes: ' + e.message);
    } finally {
      setIsCommitting(false);
    }
  };

  const cancel = async () => {
    if (hasUnsavedChanges() && !(await confirm('You have unsaved changes. Exit without saving?'))) return;
    onClose();
  };

  const startHold = async () => {
    if (!canEdit) return;
    const reason = await prompt('Reason for hold (required):', 'Place Project on Hold');
    if (reason === null) return;
    if (!reason.trim()) { alert('Reason is required.'); return; }
    try {
      await api.post(`/projects/${project.id}/holds`, { reason: reason.trim() }, user.token);
      reload();
      onProjectUpdate && onProjectUpdate();
    } catch (e) { alert('Failed to start hold: ' + e.message); }
  };

  const endHold = async (holdId) => {
    if (!canEdit) return;
    if (!(await confirm('End this hold and resume the project?'))) return;
    try {
      await api.put(`/projects/${project.id}/holds/${holdId}/end`, {}, user.token);
      reload();
      onProjectUpdate && onProjectUpdate();
    } catch (e) { alert('Failed to end hold: ' + e.message); }
  };

  const archive = async () => {
    if (!user.isAdmin) return;
    const msg = `Are you sure you want to archive project ${project.project_number} - ${project.project_name}?\n\nThis will remove it from the active project list but preserve all data.`;
    if (!(await confirm(msg, 'Archive Project'))) return;
    try {
      await api.put(`/projects/${project.id}/archive`, {}, user.token);
      onProjectUpdate && onProjectUpdate();
      onClose();
      await alert(`Project ${project.project_number} has been archived.`);
    } catch (e) {
      alert('Error archiving project: ' + e.message);
    }
  };

  if (!isOpen || !project) return null;

  const workflow = getWorkflow(projectType);
  const tasksByGroup = workflow
    ? workflow.groups.map(g => ({
        name: g.name,
        note: g.note,
        tasks: workflow.tasks.filter(t => t.group === g.name)
      }))
    : [];

  const fieldDefs = details?.field_definitions || [];
  const holds = details?.holds || [];
  const activeHold = holds.find(h => !h.ended_at);
  const today = new Date();
  const projectCreated = details?.project?.project_created || project.project_created;
  const prereqsCompletedAt = details?.project?.prereqs_completed_at;

  const sinceCreated = projectCreated ? daysBetween(projectCreated, today) : null;
  const sincePrereqs = prereqsCompletedAt ? daysBetween(prereqsCompletedAt, today) : null;

  // Third clock: type-configured interval between two custom-field dates. Holds are subtracted only
  // when the type says so (TA excludes them as paused work; Data Sheets counts them as the metric).
  const clockCfg = ui?.thirdClock;
  let thirdClockValue = null;
  let thirdClockNote = '';
  if (clockCfg) {
    const startVal = metadata[clockCfg.startField];
    const endVal = metadata[clockCfg.endField];
    if (startVal) {
      const intervalEnd = endVal || today;
      const intervalDays = daysBetween(startVal, intervalEnd);
      if (clockCfg.excludeHolds) {
        const holdDays = totalHoldDaysWithinInterval(holds, startVal, intervalEnd);
        thirdClockValue = Math.max(0, intervalDays - holdDays);
        thirdClockNote = holdDays > 0 ? `excludes ${holdDays}d hold` : 'no holds in interval';
      } else {
        thirdClockValue = intervalDays;
        thirdClockNote = endVal ? `through ${formatDate(endVal)}` : 'still open';
      }
    } else {
      thirdClockNote = clockCfg.notStartedNote;
    }
  }

  return createPortal(
    <div className="modal-overlay">
      <div className={`modal modal-xl${editMode ? '' : ' view-mode'}`}>
        <div className="ta-scroll">

          <div className="ta-modal-header">
            <h2>
              {canEdit && details ? (
                <input
                  className="input ta-name-input"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  title="Project Name"
                />
              ) : (
                project.project_name
              )}
              <span className="meta">{ui?.workflowLabel || 'Workflow'}</span>
              {hasUnsavedChanges() && <span className="unsaved-badge">• Unsaved Changes</span>}
            </h2>
            <div className="header-actions">
              {user.isAdmin && !editMode && (
                <button className="btn btn-edit-mode" onClick={() => setEditMode(true)}>Edit</button>
              )}
              <button className="close" onClick={cancel}>×</button>
            </div>
          </div>

          <div className="ta-clocks-band">
            <div className="ta-clock-cell">
              <div className="ta-clock-label">Time since created</div>
              <div className="ta-clock-value">{sinceCreated !== null ? `${sinceCreated} days` : '—'}</div>
              <div className="ta-clock-since">created {formatDate(projectCreated)}</div>
            </div>
            <div className="ta-clock-cell">
              <div className="ta-clock-label">Time since pre-reqs satisfied</div>
              <div className="ta-clock-value">{sincePrereqs !== null ? `${sincePrereqs} days` : '—'}</div>
              <div className="ta-clock-since">{prereqsCompletedAt ? `satisfied ${formatDate(prereqsCompletedAt)}` : 'not yet'}</div>
            </div>
            {clockCfg && (
              <div className="ta-clock-cell">
                <div className="ta-clock-label">{clockCfg.label}</div>
                <div className="ta-clock-value">{thirdClockValue !== null ? `${thirdClockValue} days` : '—'}</div>
                <div className="ta-clock-since">{thirdClockNote}</div>
              </div>
            )}
          </div>

          {activeHold && (
            <div className="ta-hold-banner">
              <div>
                <strong>ON HOLD</strong> since {formatDate(activeHold.started_at)} —
                <span className="reason"> "{activeHold.reason}"</span>
              </div>
              {canEdit && (
                <button className="btn btn-warning" onClick={() => endHold(activeHold.id)}>End Hold</button>
              )}
            </div>
          )}

          <div className="ta-detail-top-row">
            <div className="cluster">
              <span className="label">Status</span>
              <span className="value">{WorkflowEngine.getProjectStatus(projectType, completedTaskIds, metadata)}</span>
            </div>
            <div className="cluster">
              <span className="label">Priority</span>
              <input
                className="priority-input"
                value={priority}
                onChange={(e) => setPriority(e.target.value.slice(0, 3))}
                disabled={!canEdit}
              />
            </div>
            <div className="spacer"></div>
            {ui?.supportsHolds && !activeHold && canEdit && (
              <button className="btn btn-warning" onClick={startHold}>Place on Hold</button>
            )}
          </div>

          {loading ? (
            <div className="loading-screen">Loading project details...</div>
          ) : (
            <div className="ta-body-columns">

              <div className="ta-col">
                {tasksByGroup.map(group => (
                  <div key={group.name} className="ta-section">
                    <div className="ta-section-title">
                      {group.name}
                      {group.note && <span className="note">{group.note}</span>}
                    </div>
                    {group.tasks.map(t => {
                      const st = taskStates[t.id] || {};
                      const isPending = pendingTaskChanges.has(t.id);
                      const applies = WorkflowEngine.taskApplies(projectType, t.id, metadata);
                      const cls = ['ta-task-row',
                        !applies ? 'not-applicable' : (st.checked ? 'complete' : 'incomplete'),
                        isPending ? 'pending' : '',
                        ui?.immutableCompletions && st.checked && !isPending ? 'immutable' : ''
                      ].filter(Boolean).join(' ');
                      return (
                        <div key={t.id} className={cls}>
                          <span className="check" onClick={() => handleTaskToggle(t.id)}></span>
                          <span className="task-name">{t.name}</span>
                          {!applies && <span className="task-na">N/A</span>}
                          {applies && st.checked && st.date && (
                            <span className="task-meta">
                              <span className="date">{st.date}</span>
                              <span className="by">{st.user}</span>
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              <div className="ta-col">

                {fieldDefs.length > 0 && (
                  <div className="ta-section">
                    <div className="ta-section-title">{ui?.fieldsLabel || 'Custom Fields'}</div>
                    <div className="ta-field-grid">
                      {fieldDefs.map(def => {
                        const options = ui?.fieldOptions?.[def.field_key];
                        return (
                          <div className="field" key={def.field_key}>
                            <label className="field-label field-label-left">
                              {def.field_label}
                              {def.auto_populate_task_number !== null && <> <span className="key auto">auto</span></>}
                              {!!def.is_admin_only && <> <span className="key admin">admin</span></>}
                            </label>
                            {options ? (
                              <select
                                className="select"
                                value={metadata[def.field_key] || ''}
                                onChange={(e) => setMetadataField(def.field_key, e.target.value)}
                                disabled={!canEdit}
                              >
                                {options.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input
                                type={def.field_type === 'date' ? 'date' : 'text'}
                                className={`input${def.is_admin_only ? ' admin-only' : ''}`}
                                value={def.field_type === 'date'
                                  ? formatDateInput(metadata[def.field_key])
                                  : (metadata[def.field_key] || '')}
                                onChange={(e) => setMetadataField(def.field_key, e.target.value)}
                                disabled={!canEdit}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="ta-section">
                  <div className="ta-section-title">Project Notes</div>
                  <textarea
                    className="textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={!canEdit}
                    placeholder="Enter project notes..."
                  />
                </div>

                <div className="ta-section">
                  <div className="ta-section-title">Report Path</div>
                  <input
                    className="input"
                    style={{ width: '100%' }}
                    value={reportPath}
                    onChange={(e) => setReportPath(e.target.value)}
                    disabled={!canEdit}
                    placeholder="Paste Report Path here..."
                  />
                </div>

                {ui?.supportsHolds && (
                  <div className="ta-section">
                    <div className="ta-section-title">Hold History</div>
                    {holds.length === 0 ? (
                      <div className="empty hold-history-row" style={{ gridTemplateColumns: '1fr' }}>(no holds)</div>
                    ) : (
                      holds.map(h => {
                        const dur = h.ended_at ? daysBetween(h.started_at, h.ended_at) : null;
                        return (
                          <div key={h.id} className="hold-history-row">
                            <span>
                              {formatDate(h.started_at)} → {h.ended_at ? `${formatDate(h.ended_at)} (${dur}d)` : <em>active</em>}
                            </span>
                            <span className="reason">{h.reason}</span>
                            <span>by {h.started_by}{h.ended_by ? ` / ended by ${h.ended_by}` : ''}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

              </div>

            </div>
          )}

          <div className="ta-modal-footer">
            {user.isAdmin && (
              <button className="btn btn-archive" onClick={archive} disabled={isCommitting}>
                Archive Project
              </button>
            )}
            <div className="footer-right">
              {editMode ? (
                <>
                  <button className="btn btn-cancel" onClick={cancel} disabled={isCommitting}>
                    Exit Without Saving
                  </button>
                  <button className="btn btn-create" onClick={commit} disabled={isCommitting}>
                    {isCommitting ? 'Committing...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button className="btn" onClick={onClose}>Close</button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkflowModal;
